import clsx from "clsx";
import css from "./NotificationDetailsModal.module.css";
import Modal from "../Modal/Modal.jsx";
import FullSizeImgModal from "../FullSizeImgModal/FullSizeImgModal.jsx";
import ModalCloseIcon from "../../sharedComponents/ModalCloseIcon/ModalCloseIcon.jsx";
import { useImageModal } from "../../../utils/useImageModal.jsx";

const STATUS_LABELS = {
  open: "Відкрито",
  new: "Новий",
  in_progress: "В роботі",
  closed: "Закрито",
};

const PRIORITY_LABELS = {
  low: "Низький",
  medium: "Середній",
  high: "Високий",
};

const TICKET_TYPE_LABELS = {
  bug: "Помилка",
  proposition: "Пропозиція",
};

const formatDate = value => {
  if (!value) return "—";
  // Бекенд віддає частину дат без зони (напр. "2026-09-24T12:33:01.86") — це UTC
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  const date = new Date(hasZone ? value : `${value}Z`);
  if (isNaN(date)) return value;
  return date.toLocaleString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getVariant = notification => {
  if (notification.ticket) {
    return notification.type === "success" ? "success" : "info";
  }
  if (notification.type === "error") return "error";
  if (notification.type === "warning") return "warning";
  return "info";
};

const Row = ({ label, value }) =>
  value ? (
    <div className={css.row}>
      <span className={css.label}>{label}</span>
      <span className={css.value}>{value}</span>
    </div>
  ) : null;

export default function NotificationDetailsModal({ notification, onClose }) {
  const { isImgModalOpen, modalImage, handleImageClick, closeImgModal } =
    useImageModal();

  if (!notification) return null;

  const { ticket, extra } = notification;
  const isTariff = notification.type === "tariff_expiring";

  const images = ticket
    ? ticket.images?.length
      ? ticket.images
      : ticket.image
        ? [ticket.image]
        : []
    : [];

  const performer = [ticket?.performer_first_name, ticket?.performer_last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <Modal isOpen={!!notification} onClose={onClose}>
      <div className={clsx(css.modal, css[getVariant(notification)])}>
        <ModalCloseIcon onClose={onClose} />

        <p className={css.date}>{formatDate(notification.created_at)}</p>
        <h2 className={css.title}>{notification.title}</h2>
        <p className={css.message}>{notification.message}</p>

        {isTariff && extra && (
          <div className={css.block}>
            <Row label="Залишилось днів" value={extra.days_left} />
            <Row label="Оплачено до" value={formatDate(extra.paid_until)} />
          </div>
        )}

        {ticket && (
          <div className={css.block}>
            <div className={css.blockHeader}>
              <h3 className={css.blockTitle}>Тікет #{ticket.id}</h3>
              <span className={css.blockDate}>
                {formatDate(ticket.created_at)}
              </span>
            </div>
            <Row
              label="Тип"
              value={TICKET_TYPE_LABELS[ticket.type] ?? ticket.type}
            />
            <Row
              label="Статус"
              value={STATUS_LABELS[ticket.status] ?? ticket.status}
            />
            <Row label="Тема" value={ticket.title} />
            <Row label="Опис" value={ticket.body} />
            <Row label="Відділ" value={ticket.department} />
            <Row label="Модуль" value={ticket.module} />
            <Row
              label="Пріоритет"
              value={PRIORITY_LABELS[ticket.priority] ?? ticket.priority}
            />
            <Row label="Виконавець" value={performer} />

            {images.length > 0 && (
              <div className={css.gallery}>
                {images.map(url => (
                  <img
                    key={url}
                    src={url}
                    alt="Зображення тікета"
                    className={css.thumb}
                    onClick={() => handleImageClick(url)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <FullSizeImgModal
        isOpen={isImgModalOpen}
        imgUrl={modalImage}
        onClose={closeImgModal}
      />
    </Modal>
  );
}
